import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import InputField from "components/fields/InputField";
import { adminForgotPassword } from "api/adapters/auth/auth";
import { validateEmail } from "utils/validators";

const COOLDOWN = 30;

export default function ForgotPassword() {
  const [sEmail, setSEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  const startCountdown = () => {
    setCountdown(COOLDOWN);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  const { mutate: sendReset, isPending } = useMutation({
    mutationFn: adminForgotPassword,
    onSuccess: (res) => {
      setSuccess(res.message || "Password reset email sent successfully.");
      setError("");
      startCountdown();
    },
    onError: (err) => {
      setError(err.message || "Something went wrong. Please try again.");
      setSuccess("");
    },
  });

  const handleSubmit = () => {
    const eErr = validateEmail(sEmail);
    if (eErr) { setEmailError(eErr); return; }
    setError("");
    sendReset({ sEmail });
  };

  const isDisabled = isPending || countdown > 0 || !!emailError || !sEmail;

  return (
    <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      <div className="mt-[5vh] w-full max-w-full flex-col ml-28 items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
          Forgot Password
        </h4>
        <p className="mb-8 ml-1 text-base text-gray-600">
          Enter your registered email to receive a password reset link.
        </p>

        <InputField
          variant="auth"
          extra="mb-1"
          label="Email*"
          placeholder="admin@example.com"
          id="sEmail"
          type="email"
          value={sEmail}
          state={emailError ? "error" : undefined}
          onChange={(e) => {
            setSEmail(e.target.value);
            setEmailError(validateEmail(e.target.value));
            setError("");
            setSuccess("");
          }}
        />
        {emailError && (
          <p className="mb-3 ml-1.5 text-xs font-medium text-red-500">{emailError}</p>
        )}
        {!emailError && <div className="mb-3" />}

        {error && <p className="mb-3 text-sm font-medium text-red-500">{error}</p>}
        {success && <p className="mb-3 text-sm font-medium text-green-500">{success}</p>}

        <button
          onClick={handleSubmit}
          disabled={isDisabled}
          className="linear mt-2 w-full rounded-xl bg-[#FF7A00] py-[12px] text-base font-medium text-white transition duration-200 hover:bg-[#e66a00] active:bg-[#cc5e00] disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_4px_14px_0_rgba(255,122,0,0.39)]"
        >
          {isPending ? "Sending..." : countdown > 0 ? `Resend in ${countdown}s` : "Send Reset Link"}
        </button>

        <div className="mt-5 text-center">
          <Link to="/auth/sign-in" className="text-sm font-medium text-[#FF7A00] hover:underline dark:text-white">
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
